import { useQuery } from '@tanstack/react-query';
import { userApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';
import { getStatusCode } from 'src/api/queries/errorClassification';

const REFERENCE_STALE_TIME = 5 * 60_000;

/**
 * Admin-scoped user directory (`listAdminUsers`) — a different server
 * contract from `useUsersList`'s `getUsers` (org-wide directory), kept as its
 * own canonical key.
 *
 * A non-admin caller gets a 403 here by design, and an empty admin directory
 * is the correct *data* interpretation of that, not an error — so the 403 is
 * caught inside the queryFn and resolved as `[]` rather than left to surface
 * via `meta.expectedStatuses`. An error state is never cached: every remount
 * would re-fire a request already known to 403, and consumers would have to
 * special-case an error shape for what is really an expected authorization
 * outcome. Genuine (non-403) failures still reach the central pipeline (they
 * get logged; the load-time toast is suppressed below for legacy fail-soft
 * parity, but a manual refresh surfaces the failure via the toast in the
 * `OrganizationProvider` shim's `refreshUsers`).
 *
 * Archived users are left out unless `includeArchived` is true.
 */
export function useAdminUsers(includeArchived = false) {
  return useQuery({
    queryKey: queryKeys.organization.adminUsers(includeArchived),
    queryFn: async ({ signal }) => {
      try {
        return await userApiClient.listAdminUsers(
          includeArchived,
          toCancelToken(signal),
        );
      } catch (err) {
        if (getStatusCode(err) === 403) return [];
        throw err;
      }
    },
    staleTime: REFERENCE_STALE_TIME,
    meta: {
      suppressErrorToast: true,
      errorContext: 'organization admin users',
    },
  });
}
